class ScrollPagerHandler {
  instances = new Map();

  init(vueElement, triggerElementRefName, callbackFunctionName, params) {
    let pagers = this.instances.get(vueElement);
    if (!pagers) {
      pagers = new Map();
      this.instances.set(vueElement, pagers);
    }

    const existing = pagers.get(triggerElementRefName);
    if (existing) {
      existing.init();
      return;
    }

    pagers.set(
      triggerElementRefName,
      new ScrollPagerInstance(vueElement, triggerElementRefName, callbackFunctionName, params),
    );
  }

  // Stops observing; a later init() of the component starts it again.
  destroyAllIn(vueElement) {
    this.instances.get(vueElement)?.forEach(instance => instance.destroy());
  }

  remove(vueElement) {
    this.destroyAllIn(vueElement);
    this.instances.delete(vueElement);
  }
}

class ScrollPagerInstance {
  observer = null;

  constructor(vueElement, triggerElementRefName, callbackFunctionName, params = {}) {
    this.vueElement = vueElement;
    this.triggerElementRefName = triggerElementRefName;
    this.callbackFunctionName = callbackFunctionName;
    this.params = params;

    this.init();
  }

  // Observes the current trigger element: after a re-render it can be a new one.
  init() {
    this.destroy();

    this.vueElement.$nextTick(() => {
      if (!(this.triggerElementRefName in this.vueElement.$refs)) {
        console.warn(`"${this.triggerElementRefName}" ref not found in the component. ScrollPager will not work.`);
        return;
      }
      if (!(this.callbackFunctionName in this.vueElement)) {
        console.warn(`"${this.callbackFunctionName}" function not found in the component. ScrollPager will not work.`);
        return;
      }

      let targetElement = this.vueElement.$refs[this.triggerElementRefName];
      const originalTargetElement = targetElement;

      if (!!targetElement && !(targetElement instanceof HTMLElement)) {
        if ('length' in targetElement && targetElement.length !== 0) {
          targetElement = targetElement[0];
        }
        if (
          typeof targetElement === 'object'
          && targetElement !== null
          && !Array.isArray(targetElement)
          && '$el' in targetElement
        ) {
          targetElement = targetElement.$el;
        }
      }

      if (!targetElement || !(targetElement instanceof HTMLElement)) {
        console.error('Invalid target element for infinite scroll', originalTargetElement);
        return;
      }

      if (this._isElementInViewport(targetElement)) {
        return;
      }

      if (this.observer === null) {
        this.observer = new IntersectionObserver(entry => {
          if (entry[0].isIntersecting) {
            this.destroy();
            this.vueElement[this.callbackFunctionName]();
          }
        }, this.params);
      }

      this.observer.observe(targetElement);
    });
  }

  destroy() {
    if (this.observer !== null) {
      this.observer.disconnect();
      this.observer = null;
    }
  }

  _isElementInViewport(el) {
    const rect = el.getBoundingClientRect();
    const windowHeight = (window.innerHeight || document.documentElement.clientHeight);
    const windowWidth = (window.innerWidth || document.documentElement.clientWidth);

    const vertInView = (rect.top <= windowHeight) && ((rect.top + rect.height) >= 0);
    const horInView = (rect.left <= windowWidth) && ((rect.left + rect.width) >= 0);

    return vertInView && horInView;
  }
}

const scrollPagerHandler = new ScrollPagerHandler();

export default {
  beforeUnmount() {
    scrollPagerHandler.remove(this);
  },
  methods: {
    initScrollPager(triggerElementRefName, callbackFunctionName, params = {}) {
      scrollPagerHandler.init(this, triggerElementRefName, callbackFunctionName, params);
    },
    destroyScrollPagerObserver() {
      scrollPagerHandler.destroyAllIn(this);
    },
  },
};
